// src/pages/RulesConfigPage.jsx
import { useEffect, useState, useMemo } from "react";
import { fetchRules, createRule, updateRule, deleteRule } from "../api/sowApi";
import "../styles/rules.css";

// Built-in contract types; GLOBAL = null/empty
const BASE_CONTRACT_TYPES = ["T&M", "Fixed Bid", "Managed Services"];

// Suggested categories & dimensions
const CATEGORY_OPTIONS = [
  "Scope & Deliverables",
  "Scope & Planning",
  "Scope & Risk",
  "Commercials",
  "Quality & Acceptance",
  "Quality & Warranty",
  "Service Levels",
  "Process & Governance",
  "Risk & Legal",
];

const DIMENSION_OPTIONS = [
  "Scope Clarity",
  "Assumptions & Exclusions",
  "Assumptions & Customer Responsibilities",
  "Milestones & WBS",
  "Acceptance Criteria",
  "Billing & Invoicing",
  "Rate Card & Currency",
  "Change Control",
  "Timesheet & Approval Process",
  "T&E Treatment",
  "Termination & Notice",
  "SLAs & Credits",
  "Risk & Contingency",
  "Exit & Handover",
];

const severityToImpact = (sev) => {
  const s = (sev || "").toLowerCase();
  if (s === "critical") return 4;
  if (s === "high") return 3;
  if (s === "medium") return 2;
  return 1;
};

const clamp14 = (v, fallback) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  if (n < 1) return 1;
  if (n > 4) return 4;
  return n;
};

const emptyRule = {
  name: "",
  contract_type: "", // null/empty => GLOBAL
  category: "",
  dimension: "",
  description: "",
  severity: "medium",

  // legacy weight (still stored; auto-calculated by default)
  weight: 1,

  // new factor scoring
  impact: 2,
  likelihood: 2,
  recoverability: 2,
  applicability: 2,
  auto_weight: true,

  is_mandatory: false,
  keyword_pattern: "",
  prompt_hint: "",
  min_score: "",
  max_score: "",
  active: true,
};

export default function RulesConfigPage() {
  const [rules, setRules] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyRule);

  const [typeMode, setTypeMode] = useState("select");
  const [categoryMode, setCategoryMode] = useState("select");
  const [dimensionMode, setDimensionMode] = useState("select");

  const [customType, setCustomType] = useState("");
  const [categoryCustom, setCategoryCustom] = useState("");
  const [dimensionCustom, setDimensionCustom] = useState("");

  const [filterType, setFilterType] = useState("ALL");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const loadRules = async () => {
    try {
      setLoading(true);
      const data = await fetchRules();
      setRules(data);
    } catch (err) {
      console.error(err);
      setError("Failed to load rules.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, []);

  useEffect(() => {
    if (!message) return;
    const id = setTimeout(() => setMessage(""), 4000);
    return () => clearTimeout(id);
  }, [message]);

  const allContractTypes = useMemo(() => {
    const s = new Set(BASE_CONTRACT_TYPES);
    rules.forEach((r) => {
      if (r.contract_type) s.add(r.contract_type);
    });
    return Array.from(s);
  }, [rules]);

  // Auto set impact from severity (unless user overrides by changing impact manually)
  useEffect(() => {
    setForm((f) => {
      const desired = severityToImpact(f.severity);
      // if impact is empty/null, or equals previous mapping, keep synced
      // (simple rule: always sync impact to severity when auto_weight is ON)
      if (f.auto_weight) {
        return { ...f, impact: desired };
      }
      return f;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.severity]);

  // Auto compute weight from factors when auto_weight is ON
  useEffect(() => {
    if (!form.auto_weight) return;
    const impact = clamp14(form.impact, severityToImpact(form.severity));
    const likelihood = clamp14(form.likelihood, 2);
    const recoverability = clamp14(form.recoverability, 2);
    const applicability = clamp14(form.applicability, 2);
    const raw = impact * likelihood * recoverability * applicability;
    setForm((f) => ({ ...f, weight: raw }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.auto_weight, form.impact, form.likelihood, form.recoverability, form.applicability]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setForm((f) => ({ ...f, [name]: checked }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  };

  const onContractTypeSelectChange = (e) => {
    const value = e.target.value;
    if (value === "__CUSTOM__") {
      setTypeMode("custom");
      setCustomType(
        form.contract_type && !allContractTypes.includes(form.contract_type)
          ? form.contract_type
          : ""
      );
      setFilterType("ALL");
    } else {
      setTypeMode("select");
      setForm((f) => ({ ...f, contract_type: value }));
      setCustomType("");
      setFilterType(value || "ALL");
    }
  };

  const onCustomTypeChange = (e) => {
    const value = e.target.value;
    setTypeMode("custom");
    setCustomType(value);
    setForm((f) => ({ ...f, contract_type: value }));
    setFilterType(value.trim() ? value.trim() : "ALL");
  };

  const contractTypeSelectValue =
    typeMode === "custom" ? "__CUSTOM__" : form.contract_type || "";

  const onCategorySelectChange = (e) => {
    const value = e.target.value;
    if (value === "__CUSTOM__") {
      setCategoryMode("custom");
      setCategoryCustom(
        form.category && !CATEGORY_OPTIONS.includes(form.category)
          ? form.category
          : ""
      );
    } else {
      setCategoryMode("select");
      setForm((f) => ({ ...f, category: value }));
      setCategoryCustom("");
    }
  };

  const onCategoryCustomChange = (e) => {
    const value = e.target.value;
    setCategoryMode("custom");
    setCategoryCustom(value);
    setForm((f) => ({ ...f, category: value }));
  };

  const categorySelectValue =
    categoryMode === "custom" ? "__CUSTOM__" : form.category || "";

  const onDimensionSelectChange = (e) => {
    const value = e.target.value;
    if (value === "__CUSTOM__") {
      setDimensionMode("custom");
      setDimensionCustom(
        form.dimension && !DIMENSION_OPTIONS.includes(form.dimension)
          ? form.dimension
          : ""
      );
    } else {
      setDimensionMode("select");
      setForm((f) => ({ ...f, dimension: value }));
      setDimensionCustom("");
    }
  };

  const onDimensionCustomChange = (e) => {
    const value = e.target.value;
    setDimensionMode("custom");
    setDimensionCustom(value);
    setForm((f) => ({ ...f, dimension: value }));
  };

  const dimensionSelectValue =
    dimensionMode === "custom" ? "__CUSTOM__" : form.dimension || "";

  const onEdit = (rule) => {
    setEditingId(rule.id);

    setForm({
      ...emptyRule,
      ...rule,
      min_score: rule.min_score ?? "",
      max_score: rule.max_score ?? "",
      // default factors if missing in older rules:
      impact: rule.impact ?? severityToImpact(rule.severity),
      likelihood: rule.likelihood ?? 2,
      recoverability: rule.recoverability ?? 2,
      applicability: rule.applicability ?? 2,
      auto_weight: rule.auto_weight ?? false, // keep OFF by default for existing rules
    });

    setTypeMode("select");
    setCustomType("");

    if (rule.category && !CATEGORY_OPTIONS.includes(rule.category)) {
      setCategoryMode("custom");
      setCategoryCustom(rule.category);
    } else {
      setCategoryMode("select");
      setCategoryCustom("");
    }

    if (rule.dimension && !DIMENSION_OPTIONS.includes(rule.dimension)) {
      setDimensionMode("custom");
      setDimensionCustom(rule.dimension);
    } else {
      setDimensionMode("select");
      setDimensionCustom("");
    }

    setFilterType(rule.contract_type ? rule.contract_type : "ALL");
    setError("");
    setMessage("");
  };

  const onCancel = () => {
    setEditingId(null);
    setForm(emptyRule);
    setTypeMode("select");
    setCategoryMode("select");
    setDimensionMode("select");
    setCustomType("");
    setCategoryCustom("");
    setDimensionCustom("");
    setError("");
    setMessage("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");

    const impact = clamp14(form.impact, severityToImpact(form.severity));
    const likelihood = clamp14(form.likelihood, 2);
    const recoverability = clamp14(form.recoverability, 2);
    const applicability = clamp14(form.applicability, 2);

    const computedWeight = impact * likelihood * recoverability * applicability;

    const payload = {
      ...form,
      weight: Number(form.auto_weight ? computedWeight : form.weight) || 0,

      // store factors (backend will compute effective weight from these)
      impact,
      likelihood,
      recoverability,
      applicability,

      min_score: form.min_score === "" ? null : Number(form.min_score),
      max_score: form.max_score === "" ? null : Number(form.max_score),
      contract_type:
        form.contract_type && form.contract_type.trim() !== ""
          ? form.contract_type.trim()
          : null,
    };

    try {
      if (editingId) {
        await updateRule(editingId, payload);
        setMessage("Rule updated successfully.");
      } else {
        await createRule(payload);
        setMessage("Rule created successfully.");
      }
      await loadRules();
      onCancel();
    } catch (err) {
      console.error(err);
      setError("Failed to save rule.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this rule?")) return;
    setError("");
    setMessage("");
    try {
      await deleteRule(id);
      await loadRules();
      setMessage("Rule deleted successfully.");
    } catch (err) {
      console.error(err);
      setError("Failed to delete rule.");
    }
  };

  const availableFilterTypes = useMemo(() => {
    const s = new Set();
    s.add("GLOBAL");
    allContractTypes.forEach((t) => s.add(t));
    return Array.from(s);
  }, [allContractTypes]);

  const filteredRules = useMemo(() => {
    if (!filterType || filterType === "ALL") return rules;
    if (filterType === "GLOBAL") return rules.filter((r) => !r.contract_type);
    return rules.filter((r) => r.contract_type === filterType);
  }, [rules, filterType]);

  const impact = clamp14(form.impact, severityToImpact(form.severity));
  const likelihood = clamp14(form.likelihood, 2);
  const recoverability = clamp14(form.recoverability, 2);
  const applicability = clamp14(form.applicability, 2);
  const computedWeight = impact * likelihood * recoverability * applicability;
  const normalizedWeight = (computedWeight / 256).toFixed(4);

  return (
    <div className="rules-page">
      <header className="rules-header">
        <div>
          <h1>Rules Engine Configuration</h1>
          <p>
            Configure scoring rules for SOW analysis across contract types (T&amp;M, Fixed Bid,
            Managed Services, etc.). Scoring supports factor-based weights:
            Impact×Likelihood×Recoverability×Applicability.
          </p>
        </div>
      </header>

      {message && <div className="rules-message success">{message}</div>}
      {error && <div className="rules-error">{error}</div>}

      <div className="rules-layout">
        <section className="rules-list">
          <div className="rules-list-header">
            <h2>Existing Rules</h2>
            <div className="rules-list-controls">
              <label className="rules-filter">
                <span>Type:</span>
                <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                  <option value="ALL">All</option>
                  <option value="GLOBAL">GLOBAL</option>
                  {availableFilterTypes
                    .filter((t) => t !== "GLOBAL")
                    .map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                </select>
              </label>
              {loading && <span className="tag">Loading…</span>}
            </div>
          </div>

          <div className="rules-table-wrapper">
            <table className="rules-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Dimension</th>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Severity</th>
                  <th>Weight</th>
                  <th>Mandatory</th>
                  <th>Active</th>
                  <th className="rules-actions-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRules.map((r) => (
                  <tr key={r.id}>
                    <td>{r.contract_type || "GLOBAL"}</td>
                    <td>{r.dimension}</td>
                    <td>{r.name}</td>
                    <td>{r.category}</td>
                    <td className={`severity severity-${r.severity}`}>{r.severity}</td>
                    <td>{r.weight}</td>
                    <td>{r.is_mandatory ? "Yes" : "No"}</td>
                    <td>{r.active ? "Yes" : "No"}</td>
                    <td className="rules-actions">
                      <button type="button" onClick={() => onEdit(r)}>Edit</button>
                      <button type="button" className="danger" onClick={() => onDelete(r.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredRules.length === 0 && !loading && (
                  <tr>
                    <td colSpan="9" className="empty-cell">
                      No rules found for this filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rules-form">
          <h2>{editingId ? "Edit Rule" : "Add New Rule"}</h2>
          <form onSubmit={onSubmit}>
            <div className="form-row">
              <label>
                Name (Rule Key)
                <input
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  placeholder="e.g. TM_SCOPE_BOUNDARY, FB_ACCEPTANCE_CRITERIA"
                />
              </label>
            </div>

            <div className="form-row two-cols">
              <label>
                Contract Type
                <select value={contractTypeSelectValue} onChange={onContractTypeSelectChange}>
                  <option value="">GLOBAL (applies to any contract type)</option>
                  {allContractTypes.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                  <option value="__CUSTOM__">+ Custom…</option>
                </select>
              </label>

              {typeMode === "custom" && (
                <label>
                  Custom Type
                  <input
                    value={customType}
                    onChange={onCustomTypeChange}
                    placeholder="e.g. Outcome-Based"
                  />
                </label>
              )}
            </div>

            <div className="form-row two-cols">
              <label>
                Category
                <select value={categorySelectValue} onChange={onCategorySelectChange}>
                  <option value="">Select category…</option>
                  {CATEGORY_OPTIONS.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                  <option value="__CUSTOM__">+ Custom…</option>
                </select>
              </label>
              {categoryMode === "custom" && (
                <label>
                  Custom Category
                  <input value={categoryCustom} onChange={onCategoryCustomChange} />
                </label>
              )}
            </div>

            <div className="form-row two-cols">
              <label>
                Dimension
                <select value={dimensionSelectValue} onChange={onDimensionSelectChange}>
                  <option value="">Select dimension…</option>
                  {DIMENSION_OPTIONS.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                  <option value="__CUSTOM__">+ Custom…</option>
                </select>
              </label>
              {dimensionMode === "custom" && (
                <label>
                  Custom Dimension
                  <input value={dimensionCustom} onChange={onDimensionCustomChange} />
                </label>
              )}
            </div>

            <div className="form-row">
              <label>
                Description
                <textarea
                  name="description"
                  value={form.description}
                  onChange={onChange}
                  rows={3}
                />
              </label>
            </div>

            <div className="form-row two-cols">
              <label>
                Severity
                <select name="severity" value={form.severity} onChange={onChange}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </label>

              <label className="checkbox" style={{ alignSelf: "end" }}>
                <input
                  type="checkbox"
                  name="auto_weight"
                  checked={!!form.auto_weight}
                  onChange={onChange}
                />
                <span>Auto-calc weight from factors</span>
              </label>
            </div>

            <div className="form-row two-cols">
              <label>
                Impact (1–4)
                <input
                  type="number"
                  name="impact"
                  min="1"
                  max="4"
                  step="1"
                  value={form.impact}
                  onChange={onChange}
                  disabled={!!form.auto_weight}
                />
              </label>
              <label>
                Likelihood (1–4)
                <input
                  type="number"
                  name="likelihood"
                  min="1"
                  max="4"
                  step="1"
                  value={form.likelihood}
                  onChange={onChange}
                />
              </label>
            </div>

            <div className="form-row two-cols">
              <label>
                Recoverability (1–4)
                <input
                  type="number"
                  name="recoverability"
                  min="1"
                  max="4"
                  step="1"
                  value={form.recoverability}
                  onChange={onChange}
                />
              </label>
              <label>
                Applicability (1–4)
                <input
                  type="number"
                  name="applicability"
                  min="1"
                  max="4"
                  step="1"
                  value={form.applicability}
                  onChange={onChange}
                />
              </label>
            </div>

            <div className="form-row two-cols">
              <label>
                Weight (stored)
                <input
                  type="number"
                  name="weight"
                  value={form.auto_weight ? computedWeight : form.weight}
                  onChange={onChange}
                  disabled={!!form.auto_weight}
                />
              </label>
              <label>
                Computed Weight (raw / normalized)
                <input
                  value={`${computedWeight}  /  ${normalizedWeight}`}
                  readOnly
                />
              </label>
            </div>

            <div className="form-row two-cols">
              <label>
                Min Score (optional)
                <input type="number" name="min_score" value={form.min_score} onChange={onChange} min="0" max="100" />
              </label>
              <label>
                Max Score (optional)
                <input type="number" name="max_score" value={form.max_score} onChange={onChange} min="0" max="100" />
              </label>
            </div>

            <div className="form-row">
              <label>
                Keyword Pattern (optional)
                <input name="keyword_pattern" value={form.keyword_pattern} onChange={onChange} />
              </label>
            </div>

            <div className="form-row">
              <label>
                Prompt Hint (optional)
                <textarea name="prompt_hint" value={form.prompt_hint} onChange={onChange} rows={3} />
              </label>
            </div>

            <div className="form-row toggles">
              <label className="checkbox">
                <input type="checkbox" name="is_mandatory" checked={form.is_mandatory} onChange={onChange} />
                <span>Mandatory rule</span>
              </label>
              <label className="checkbox">
                <input type="checkbox" name="active" checked={form.active} onChange={onChange} />
                <span>Active</span>
              </label>
            </div>

            <div className="form-actions">
              <button type="submit" disabled={saving}>
                {saving ? "Saving..." : editingId ? "Update Rule" : "Create Rule"}
              </button>
              {editingId && (
                <button type="button" className="secondary" onClick={onCancel} disabled={saving}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}
